/**
 * The pieces a run's page is made of, whichever kind of run it is.
 *
 * A client-simulated challenge's unit is the submission; a server-simulated one's is the
 * session, of which the submission is a field. The two pages differ in what they are about
 * and what they poll, and agree on how a result is shown -- the tiles, the plot, the record
 * -- so that part lives here and each page composes it.
 */

import {
  AlertTriangle,
  Award,
  Check,
  Compass,
  Copy,
  Crosshair,
  EyeOff,
  Footprints,
  Timer,
  Trophy,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { ButtonLink } from "@/components/button-link";
import { Tile } from "@/components/challenge/challenge-parts";
import { ErrorState } from "@/components/data-state";
import { GoalBadge } from "@/components/goal-badge";
import { ScoreCell } from "@/components/score-cell";
import { TrajectoryPlayer } from "@/components/trajectory-player";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAsync } from "@/hooks/use-async";
import type {
  Arrival,
  Challenge,
  GoalTier,
  RubricSpec,
  Score,
  SimSession,
  Submission,
} from "@/lib/api";
import { studentApi } from "@/lib/api";
import {
  formatDegrees,
  formatError,
  formatRelative,
  formatSeconds,
  formatSteps,
  shortId,
} from "@/lib/format";
import { formatPoints, formatSpl } from "@/lib/scoring";
import { cn } from "@/lib/utils";

/**
 * Sample cap requested for playback.
 *
 * Higher than the API default because the animation is smoother the denser the
 * series, and the trail length scales to whatever it receives. Still bounded, so
 * a 30,000-step recording does not ship an unbounded payload.
 */
export const PLAYBACK_MAX_POINTS = 20000;

/** The run this one is measured against, and where to read about it. */
export interface BestRef {
  tier: GoalTier | null;
  steps: number | null;
  points: number;
  at: number;
  href: string;
}

export function NeighbourLink({
  to,
  label,
  children,
}: {
  to: string | null;
  label: string;
  children: ReactNode;
}) {
  if (!to) {
    return (
      <Button variant="outline" size="sm" disabled aria-label={label}>
        {children}
      </Button>
    );
  }
  return (
    <ButtonLink variant="outline" size="sm" to={to} aria-label={label}>
      {children}
    </ButtonLink>
  );
}

/**
 * The numbers that say how the run went, each with what the rubric made of it.
 *
 * The score comes first because it is what ranks; the metrics after it are what it was
 * made from. Every hint is read off the server's `Score` -- which rule paid in which block
 * -- rather than re-derived here: the rubric is evaluated in one place.
 */
export function ResultTiles({
  challenge,
  arrival,
  steps,
  spl = null,
  navTimeS = null,
  session = null,
  score,
  best,
  isBest,
  attempts,
}: {
  challenge: Challenge;
  /** Distance and heading at check-in; null for a miss, which is not told how near it came. */
  arrival: Arrival | null;
  /** Null for an upload, whose maze has no shortest route to compare against. */
  spl?: number | null;
  steps: number | null;
  /** Client-simulated only: the student's wall clock, as they reported it. */
  navTimeS?: number | null;
  session?: SimSession | null;
  /** The server's verdict; null while unscored. */
  score: Score | null;
  best: BestRef | null;
  isBest: boolean;
  attempts: number;
}) {
  const spec = challenge.rubric_spec;
  const isServerSim = challenge.simulator === "server";
  const showHeading = isServerSim || arrival?.heading_deg != null;
  const tiles = showHeading ? 6 : 5;
  const reached = score?.reached ?? null;

  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-3 md:grid-cols-3",
        tiles === 6 ? "xl:grid-cols-6" : "xl:grid-cols-5",
      )}
    >
      <Tile icon={<Award className="size-3.5" aria-hidden />} label="Score">
        {score ? (
          <>
            <ScoreCell points={score.points} max={score.max_points} className="text-base" />
            <Hint>
              {score.reached
                ? spec.blocks
                    .map((b) => `${b.title.toLowerCase()} ${formatPoints(score.awarded[b.title] ?? 0)}`)
                    .join(" · ")
                : "goal missed — nothing pays"}
            </Hint>
          </>
        ) : (
          <>
            —<Hint>not scored</Hint>
          </>
        )}
      </Tile>

      <Tile
        icon={<Crosshair className="size-3.5" aria-hidden />}
        label="Distance to goal"
        tone={reached === false ? "danger" : undefined}
      >
        <span className="font-mono">{formatError(arrival?.distance_m ?? null)}</span>
        <Hint>
          {reached === null
            ? "\u00a0"
            : reached
              ? `Within the goal (${spec.goal})${ruleHint(spec, score!, 0)}`
              : `Outside the goal (${spec.goal}) · how far is not shown for a miss`}
        </Hint>
      </Tile>

      {showHeading ? (
        <Tile icon={<Compass className="size-3.5" aria-hidden />} label="Heading error">
          <span className="font-mono">{formatDegrees(arrival?.heading_deg ?? null)}</span>
          <Hint>
            {reached === false
              ? "shown once a run reaches the goal"
              : arrival?.heading_deg == null
                ? "not measured for this run"
                : blockHint(spec, score, "heading", "off target view 0")}
          </Hint>
        </Tile>
      ) : null}

      <Tile icon={<Footprints className="size-3.5" aria-hidden />} label="Nav steps">
        <span className="font-mono">{formatSteps(steps)}</span>
        {spl !== null ? (
          <span className="text-muted-foreground font-mono text-xs"> · SPL {formatSpl(spl)}</span>
        ) : null}
        <Hint>
          {spl !== null && spec.variables.includes("spl")
            ? blockHint(spec, score, "spl", "success weighted by path length")
            : isServerSim
              ? `of a ${challenge.max_steps.toLocaleString()} step budget`
              : best && !isBest && best.steps !== null && steps !== null
                ? steps < best.steps
                  ? `${(best.steps - steps).toLocaleString()} fewer than your best`
                  : `${(steps - best.steps).toLocaleString()} more than your best`
                : "\u00a0"}
        </Hint>
      </Tile>

      {isServerSim ? (
        <Tile icon={<Timer className="size-3.5" aria-hidden />} label="Time">
          <span className="font-mono">
            {session?.timing.wall_ms != null ? formatSeconds(session.timing.wall_ms / 1000) : "—"}
          </span>
          <Hint>
            {steps === null ? "" : `${formatSeconds(steps / 100)} of robot time`}
            {session?.timing.think_mean_ms != null
              ? ` · ${session.timing.think_mean_ms.toFixed(0)} ms per step thinking`
              : ""}
          </Hint>
        </Tile>
      ) : (
        <Tile icon={<Timer className="size-3.5" aria-hidden />} label="Time">
          <span className="font-mono">{formatSeconds(navTimeS)}</span>
          <Hint>
            {navTimeS !== null && steps
              ? `${((navTimeS * 1000) / steps).toFixed(0)} ms per step · not ranked`
              : "\u00a0"}
          </Hint>
        </Tile>
      )}

      <Tile icon={<Trophy className="size-3.5" aria-hidden />} label="Your best">
        {isBest ? (
          <>
            This run
            <Hint>
              {attempts > 1 ? `the one that counts, of ${attempts}` : "your only attempt so far"}
            </Hint>
          </>
        ) : best ? (
          <>
            <Link
              to={best.href}
              className="flex items-center gap-2 underline-offset-4 hover:underline"
            >
              <GoalBadge tier={best.tier} />
              <ScoreCell points={best.points} max={spec.max_points} className="text-xs" />
            </Link>
            <Hint>{formatRelative(best.at)}</Hint>
          </>
        ) : (
          <>
            —<Hint>no scored run yet</Hint>
          </>
        )}
      </Tile>
    </div>
  );
}

/** " · earns 6 (distance ≤ 0.1)" for the rule that paid in block `index`, or nothing. */
function ruleHint(spec: RubricSpec, score: Score, index: number): string {
  const block = spec.blocks[index];
  if (!block) return "";
  const hit = score.matched[block.title];
  if (hit === null || hit === undefined) return " · no rule matched";
  const rule = block.rules[hit];
  return ` · earns ${rule.points} (${rule.condition})`;
}

/**
 * What the blocks mentioning `variable` made of the run: "Earns 2 (heading ≤ 15°)",
 * "No rule matched", "Would count — but the goal was missed", or "not graded here".
 */
function blockHint(spec: RubricSpec, score: Score | null, variable: string, unit: string): string {
  const blocks = spec.blocks.filter((b) => b.rules.some((r) => r.condition.includes(variable)));
  if (blocks.length === 0) return `${unit} · not graded on this challenge`;
  if (!score) return unit;
  if (!score.reached) return `${unit} · would count, but the goal was missed`;
  const parts = blocks.map((b) => {
    const hit = score.matched[b.title];
    if (hit === null || hit === undefined) return `${b.title}: no rule matched`;
    const rule = b.rules[hit];
    return `earns ${rule.points} (${rule.condition})`;
  });
  return parts.join(" · ");
}

export function Hint({ children }: { children: ReactNode }) {
  return (
    <span className="text-muted-foreground mt-0.5 block text-xs font-normal">
      {children}
    </span>
  );
}

export function FailureNotice({ sub }: { sub: Submission }) {
  const mazeMismatch = sub.error_msg?.includes("Maze mismatch") ?? false;
  return (
    <div className="border-destructive/30 bg-destructive/5 space-y-2 rounded-lg border px-4 py-3 text-sm">
      <p className="text-destructive flex items-center gap-2 font-medium">
        <AlertTriangle className="size-4 shrink-0" aria-hidden />
        This run was rejected and did not score
      </p>
      {sub.error_msg ? (
        <pre className="text-destructive/90 font-mono text-xs whitespace-pre-wrap">
          {sub.error_msg}
        </pre>
      ) : null}
      {mazeMismatch ? (
        <p className="text-muted-foreground text-xs">
          The recording was made on a different maze from this challenge. Replace{" "}
          <code className="font-mono">startup.json</code> with the one on the challenge
          page, delete <code className="font-mono">data/maze.json</code>, and record
          again. This attempt does not count against you.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The replay, fetched here so the page only has to say which recording.
 *
 * `jobId` is null for a run that left no recording -- a session that ended without
 * `checkin()` -- and `ready` is false while there is nothing to draw yet. The player is
 * small on purpose: a maze at 26rem reads fine, and at 40rem it swallowed the page.
 */
export function TrajectorySection({
  challenge,
  jobId,
  ready,
  apiKey,
  note,
  waiting = "Waiting for evaluation…",
}: {
  challenge: Challenge;
  jobId: string | null;
  ready: boolean;
  apiKey: string;
  note?: ReactNode;
  waiting?: string;
}) {
  const canPlot = challenge.show_trajectory;
  const trajectory = useAsync(
    canPlot && ready && jobId
      ? () => studentApi.trajectory(jobId, apiKey, PLAYBACK_MAX_POINTS)
      : null,
    [jobId, apiKey, canPlot, ready],
  );
  const walls = useAsync(
    canPlot && jobId ? () => studentApi.walls(challenge.id, apiKey) : null,
    [challenge.id, apiKey, canPlot, jobId],
  );

  return (
    <section className="min-w-0 space-y-2">
      <SectionLabel>
        Trajectory
        {note ? (
          <span className="text-muted-foreground font-normal normal-case tracking-normal">
            {" "}
            · {note}
          </span>
        ) : null}
      </SectionLabel>

      {!canPlot ? (
        <Placeholder icon={<EyeOff className="size-5" aria-hidden />}>
          Trajectory playback is hidden for this challenge.
        </Placeholder>
      ) : jobId === null ? (
        <Placeholder short>
          No recording — the run ended before <code className="font-mono">checkin()</code>.
        </Placeholder>
      ) : !ready ? (
        <Placeholder>{waiting}</Placeholder>
      ) : trajectory.error ? (
        <ErrorState error={trajectory.error} onRetry={trajectory.refetch} />
      ) : !trajectory.data ? (
        <Skeleton className="aspect-square w-full max-w-[26rem]" />
      ) : (
        <TrajectoryPlayer
          walls={walls.data ?? []}
          trajectory={trajectory.data}
          timeUnit={challenge.simulator === "server" ? "steps" : "seconds"}
          maxSize="26rem"
          className="max-w-[26rem]"
        />
      )}
    </section>
  );
}

function Placeholder({
  icon,
  short = false,
  children,
}: {
  icon?: ReactNode;
  /** A line of text rather than a square the size of the plot it stands in for. */
  short?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "text-muted-foreground flex max-w-[26rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center text-sm",
        short ? "py-10" : "aspect-square",
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}

/** The record: ids, timestamps, and the session's own fields where there is one. */
export function DetailsSection({ children }: { children: ReactNode }) {
  return (
    <section className="min-w-0 space-y-2">
      <SectionLabel>Details</SectionLabel>
      <dl className="grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-lg border p-4 text-sm">
        {children}
      </dl>
    </section>
  );
}

export function SessionFields({
  session,
  challenge,
}: {
  session: SimSession;
  challenge: Challenge;
}) {
  return (
    <>
      <Field label="Session">
        <CopyableId value={session.id} />
      </Field>
      <Field label="Steps used">
        <span className="font-mono">
          {session.steps_used.toLocaleString()}
          <span className="text-muted-foreground">
            {" / "}
            {challenge.max_steps.toLocaleString()}
          </span>
        </span>
      </Field>
      <Field label="Ended">
        <span className="min-w-0">
          <span className="capitalize">{session.status}</span>
          {session.end_reason ? (
            <span className="text-muted-foreground block truncate text-xs leading-5">
              {session.end_reason}
            </span>
          ) : null}
        </span>
      </Field>
    </>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground truncate text-xs leading-6">{label}</dt>
      <dd className="flex min-w-0 items-center leading-6">{children}</dd>
    </>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
      {children}
    </h2>
  );
}

export function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  }
  return (
    <span className="flex min-w-0 items-center gap-1">
      <span className="truncate font-mono text-xs" title={value}>
        {shortId(value)}…
      </span>
      <button
        type="button"
        onClick={copy}
        aria-label="Copy full id"
        className={cn(
          "text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors",
          copied && "text-emerald-600",
        )}
      >
        {copied ? (
          <Check className="size-3.5" aria-hidden />
        ) : (
          <Copy className="size-3.5" aria-hidden />
        )}
      </button>
    </span>
  );
}
